import { getEnEmailRequiredAndFormatErrorMessage } from '@/utils/enValidator';
import { MAX_EMAIL_LENGTH, MAX_NAME_LENGTH, MAX_PHONE_NUMBER_LENGTH } from '@/wabunka-src/common/features/Reservation/core/requiredFields/fieldLimits';
import type { WabunkaReservationFormStep2 } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaReservations';

export interface RequiredFieldError {
  /** 送信時の検証（validateWabunkaReservationFormStep2）と同じキー。欄のエラー表示はこのキーで引く */
  fieldName: string;
  message: string;
  /** 何も入力されていない。空欄は見れば分かるので、新動線でも送信前には理由を出さない */
  isBlank: boolean;
}

/** 通らない欄ごとに理由を返す。すべて通れば空配列 */
export type RequiredFieldCheck = (step2: WabunkaReservationFormStep2) => RequiredFieldError[];

const checkFilledWithin = (fieldName: string, value: string, maxLength: number, blankMessage: string): RequiredFieldError[] => {
  if (value === '') return [{ fieldName, message: blankMessage, isBlank: true }];
  if (value.length > maxLength) return [{ fieldName, message: `${maxLength} characters or less`, isBlank: false }];
  return [];
};

const checkName = (fieldName: string, name: string): RequiredFieldError[] => checkFilledWithin(fieldName, name, MAX_NAME_LENGTH, 'Required fields');

const checkEmail = (fieldName: string, email: string): RequiredFieldError[] => {
  const message = getEnEmailRequiredAndFormatErrorMessage(email) ?? (email.length > MAX_EMAIL_LENGTH ? `${MAX_EMAIL_LENGTH} characters or less` : undefined);
  return message === undefined ? [] : [{ fieldName, message, isBlank: email === '' }];
};

export const checkBookerName: RequiredFieldCheck = (step2) => [...checkName('step2.firstName', step2.firstName), ...checkName('step2.lastName', step2.lastName)];

export const checkBookerEmail: RequiredFieldCheck = (step2) => checkEmail('step2.email', step2.email);

export const checkParticipantName: RequiredFieldCheck = (step2) =>
  step2.shouldShowSomeoneElseForm ? [...checkName('step2.participantFirstName', step2.participantFirstName), ...checkName('step2.participantLastName', step2.participantLastName)] : [];

export const checkParticipantEmail: RequiredFieldCheck = (step2) => (step2.shouldShowSomeoneElseForm ? checkEmail('step2.forSomeoneElseEmail', step2.forSomeoneElseEmail) : []);

export const checkPhoneNumber: RequiredFieldCheck = (step2) =>
  step2.shouldShowSomeoneElseForm
    ? checkFilledWithin('step2.forSomeoneElsePhoneNumber', step2.forSomeoneElsePhoneNumber, MAX_PHONE_NUMBER_LENGTH, 'Please enter phone number')
    : checkFilledWithin('step2.forMyselfPhoneNumber', step2.forMyselfPhoneNumber, MAX_PHONE_NUMBER_LENGTH, 'Please enter phone number');

export const checkNationality: RequiredFieldCheck = (step2) => {
  const [fieldName, nationality] = step2.shouldShowSomeoneElseForm ? ['step2.forSomeoneElseNationality', step2.forSomeoneElseNationality] : ['step2.forMyselfNationality', step2.forMyselfNationality];
  return nationality === '' ? [{ fieldName, message: 'Please select your Nationality.', isBlank: true }] : [];
};
